import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import Datepicker from "components/Input/Datepicker/Datepicker";
import paymentGatewayOptions from "utils/paymentGatewayOptions/PaymentGatewayOptions.jsx";
import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions.jsx";
import s from "./CreateFormContent.module.scss";

const CreateFormContent = ({ handleInput }) => {
  const { t } = useTranslation();
  const [inputData, setInputData] = useState({
    settleCurrency: "",
    paymentGateway: "",
  });
  const [focus, setFocus] = useState({});

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    handleInput(value, field);
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field) => {
    setFocus({ [field]: false });
  };

  const settlementCurrencyOnChange = (value) => {
    setInputData({ ...inputData, settleCurrency: value });
    return value;
  };

  const transDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
      ? ""
      : null;
    if (type === "start") {
      handleInput(date, "transDate");
    }
  };

  const settleDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
      ? ""
      : null;
    if (type === "start") {
      handleInput(date, "settleDate");
    }
  };

  const paymentGatewayOnChange = (value) => {
    setInputData({ ...inputData, paymentGateway: value });
    return value;
  };

  return (
    <div className={s.contentContainer}>
      <div>
        <OutlinedText
          label={t(
            "page.finance.settlement.plan.all.create.function.dialog.content.1",
          )}
          mandatory
          noErrorMessage
          onchangeFn={(e) => textInputOnchange(e, "merchantId")}
          onfocusFn={() => textInputOnFocus("merchantId")}
          onblurFn={() => textInputOnBlur("merchantId")}
          sxParams={{
            textFocus: focus.merchantId,
          }}
        />
      </div>

      <div>
        <OutlinedText
          label={t(
            "page.finance.settlement.plan.all.create.function.dialog.content.2",
          )}
          mandatory
          noErrorMessage
          onchangeFn={(e) => textInputOnchange(e, "totalServiceFee")}
          onfocusFn={() => textInputOnFocus("totalServiceFee")}
          onblurFn={() => textInputOnBlur("totalServiceFee")}
          sxParams={{ textFocus: focus.totalServiceFee }}
        />
      </div>

      <div>
        <OutlinedText
          label={t(
            "page.finance.settlement.plan.all.create.function.dialog.content.3",
          )}
          mandatory
          noErrorMessage
          onchangeFn={(e) => textInputOnchange(e, "totalChangeFee")}
          onfocusFn={() => textInputOnFocus("totalChangeFee")}
          onblurFn={() => textInputOnBlur("totalChangeFee")}
          sxParams={{ textFocus: focus.totalChangeFee }}
        />
      </div>

      <div>
        <Selection
          label={t(
            "page.finance.settlement.plan.all.create.function.dialog.content.4",
          )}
          mandatory
          noErrorMessage
          selectionValue={inputData.settleCurrency}
          onchangeFn={(e) =>
            textInputOnchange(e, "settleCurrency", settlementCurrencyOnChange)
          }
          onfocusFn={() => textInputOnFocus("settleCurrency")}
          onblurFn={() => textInputOnBlur("settleCurrency")}
          options={transactionCurrencyOptions("all")}
          sxParams={{ selectionFocus: focus.settleCurrency }}
        />
      </div>

      <div>
        <OutlinedText
          label={t(
            "page.finance.settlement.plan.all.create.function.dialog.content.5",
          )}
          mandatory
          noErrorMessage
          onchangeFn={(e) => textInputOnchange(e, "totalTransFee")}
          onfocusFn={() => textInputOnFocus("totalTransFee")}
          onblurFn={() => textInputOnBlur("totalTransFee")}
          sxParams={{ textFocus: focus.totalTransFee }}
        />
      </div>

      <div>
        <OutlinedText
          label={t(
            "page.finance.settlement.plan.all.create.function.dialog.content.6",
          )}
          mandatory
          noErrorMessage
          onchangeFn={(e) => textInputOnchange(e, "totalNet")}
          onfocusFn={() => textInputOnFocus("totalNet")}
          onblurFn={() => textInputOnBlur("totalNet")}
          sxParams={{ textFocus: focus.totalNet }}
        />
      </div>

      <div>
        <Datepicker
          label={t(
            "page.finance.settlement.plan.all.create.function.dialog.content.7",
          )}
          type="start"
          noErrorMessage
          onchangeFn={transDateOnChange}
          onfocusFn={() => textInputOnFocus("transDate")}
          onblurFn={() => textInputOnBlur("transDate")}
          sxParams={{ datepickerFocus: focus.transDate }}
        />
      </div>

      <div>
        <Datepicker
          label={t(
            "page.finance.settlement.plan.all.create.function.dialog.content.8",
          )}
          type="start"
          noErrorMessage
          onchangeFn={settleDateOnChange}
          onfocusFn={() => textInputOnFocus("settleDate")}
          onblurFn={() => textInputOnBlur("settleDate")}
          sxParams={{ datepickerFocus: focus.settleDate }}
        />
      </div>

      <div>
        <Selection
          label={t(
            "page.finance.settlement.plan.all.create.function.dialog.content.9",
          )}
          noErrorMessage
          selectionValue={inputData.paymentGateway}
          onchangeFn={(e) =>
            textInputOnchange(e, "paymentGateway", paymentGatewayOnChange)
          }
          onfocusFn={() => textInputOnFocus("paymentGateway")}
          onblurFn={() => textInputOnBlur("paymentGateway")}
          options={paymentGatewayOptions("settlement")}
          sxParams={{ selectionFocus: focus.paymentGateway }}
        />
      </div>

      <div>
        <OutlinedText
          label={t(
            "page.finance.settlement.plan.all.create.function.dialog.content.10",
          )}
          mandatory
          noErrorMessage
          onchangeFn={(e) => textInputOnchange(e, "changeReason")}
          onfocusFn={() => textInputOnFocus("changeReason")}
          onblurFn={() => textInputOnBlur("changeReason")}
          sxParams={{ textFocus: focus.changeReason }}
        />
      </div>
    </div>
  );
};

export default React.memo(CreateFormContent);
